import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Flex, Heading, Text, Input, InputGroup, InputLeftElement,
  SimpleGrid, Badge, Button, IconButton, useColorModeValue, Spinner,
  Menu, MenuButton, MenuList, MenuItem, Portal, Image, Stack, HStack, VStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Tabs, TabList, Tab, Tooltip, useToast, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFilter, faQuestionCircle, faCheckCircle, faTimesCircle,
  faImage, faFolderOpen, faLayerGroup, faLightbulb, faCopy, faEye,
  faGraduationCap, faCheck, faArrowUpRightFromSquare, faTag, faListCheck, faMusic
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { apiGetRequest } from '@/functions/requests';
import { useS3Client } from '@/hooks/contextHooks';
import SignedImage, { getSignedS3Url } from '@/components/signedImage';
import QuizAudioPlayer from '@/components/quizAudioPlayer';
import { AppMeta, NewContentItem, Content, QuizQuestion } from '@xnav/types/src/common';
import Loading from '@/components/loading';

type GlobalQuestionItem = QuizQuestion & {
  courseId: string;
  courseName: string;
  courseCreator: string;
  quizTitle: string;
  quizId: string;
};

const MotionBox = motion(Box);

export default function QuestionBank() {
  const navigate = useNavigate();
  const toast = useToast();
  const s3 = useS3Client();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<GlobalQuestionItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string>('All');
  const [selectedCourse, setSelectedCourse] = useState<string>('All');
  const [activeTab, setActiveTab] = useState<'all' | 'images' | 'audio' | 'textOnly'>('all');

  // Interactive Question Test Modal
  const [testingQuestion, setTestingQuestion] = useState<GlobalQuestionItem | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);

  // Zoom Image Modal
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);

  const glassBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(26, 32, 44, 0.85)');
  const glassBorder = useColorModeValue('1px solid rgba(226, 232, 240, 0.8)', '1px solid rgba(45, 55, 72, 0.8)');
  const titleColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    fetchGlobalQuestions();
  }, []);

  const fetchGlobalQuestions = async () => {
    setLoading(true);
    try {
      const apps = await apiGetRequest<undefined, AppMeta[]>('/apps');
      if (!apps || apps.length === 0) {
        setQuestions([]);
        setLoading(false);
        return;
      }

      const allQuestions: GlobalQuestionItem[] = [];

      await Promise.all(
        apps.map(async (app) => {
          try {
            const structure = await apiGetRequest<undefined, NewContentItem[]>(`/structure/${app.id}`);
            const quizRefs: { id: string; title: string }[] = [];

            const findQuizzes = (items: any[]) => {
              if (!Array.isArray(items)) return;
              items.forEach((item) => {
                if (item.type === 'quiz') {
                  quizRefs.push({ id: item.id, title: item.title || 'Chapter Quiz' });
                } else if (item.contents && Array.isArray(item.contents)) {
                  findQuizzes(item.contents);
                }
              });
            };

            if (structure) findQuizzes(structure);
            // Include main course quiz
            quizRefs.push({ id: `${app.id}-quiz`, title: 'Course Quiz' });

            const contentPromises = quizRefs.map((q) =>
              apiGetRequest<undefined, Content | string>(`/content/${app.id}/${q.id}`).catch(() => null)
            );
            const contents = await Promise.all(contentPromises);

            quizRefs.forEach((qRef, idx) => {
              const res = contents[idx];
              if (!res) return;

              let parsed: Content | null = null;
              if (typeof res === 'string') {
                try {
                  parsed = JSON.parse(res);
                } catch (e) {}
              } else if (typeof res === 'object') {
                parsed = res as Content;
              }

              if (parsed?.quizData?.questions && Array.isArray(parsed.quizData.questions)) {
                parsed.quizData.questions.forEach((q) => {
                  allQuestions.push({
                    ...q,
                    courseId: app.id,
                    courseName: app.name,
                    courseCreator: app.creator || 'Instructor',
                    quizTitle: qRef.title,
                    quizId: qRef.id
                  });
                });
              }
            });
          } catch (e) {
            console.error(`Error loading quizzes for app ${app.name}`, e);
          }
        })
      );

      setQuestions(allQuestions);
    } catch (e) {
      console.error('Error fetching global questions', e);
    } finally {
      setLoading(false);
    }
  };

  const uniqueCourses = useMemo(() => {
    const map = new Map<string, string>();
    questions.forEach((q) => {
      map.set(q.courseId, q.courseName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [questions]);

  // Overall Stats
  const stats = useMemo(() => {
    let beginner = 0;
    let intermediate = 0;
    let advanced = 0;
    let withImages = 0;
    let withAudio = 0;

    questions.forEach((q) => {
      const lvl = (q.level || 'Beginner').toLowerCase();
      if (lvl.includes('adv')) advanced++;
      else if (lvl.includes('int')) intermediate++;
      else beginner++;

      if (q.images && q.images.length > 0) withImages++;
      if (q.audioUrl) withAudio++;
    });

    return {
      total: questions.length,
      beginner,
      intermediate,
      advanced,
      withImages,
      withAudio
    };
  }, [questions]);

  // Filtered List
  const filteredQuestions = useMemo(() => {
    let list = [...questions];

    if (activeTab === 'images') {
      list = list.filter((q) => q.images && q.images.length > 0);
    } else if (activeTab === 'audio') {
      list = list.filter((q) => !!q.audioUrl);
    } else if (activeTab === 'textOnly') {
      list = list.filter((q) => (!q.images || q.images.length === 0) && !q.audioUrl);
    }

    if (selectedLevel !== 'All') {
      list = list.filter((q) => (q.level || 'Beginner').toLowerCase() === selectedLevel.toLowerCase());
    }

    if (selectedCourse !== 'All') {
      list = list.filter((q) => q.courseId === selectedCourse);
    }

    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (item) =>
          item.text.toLowerCase().includes(q) ||
          item.explanation?.toLowerCase().includes(q) ||
          item.courseName.toLowerCase().includes(q) ||
          (item.options && item.options.some((opt) => opt.toLowerCase().includes(q))) ||
          (item.tags && item.tags.some((tag) => tag.toLowerCase().includes(q)))
      );
    }

    return list;
  }, [questions, activeTab, selectedLevel, selectedCourse, searchQuery]);

  const copyQuestionText = (q: GlobalQuestionItem) => {
    const optionsText = q.options.map((opt, i) => `${i === q.correctAnswer ? '✓' : '•'} ${opt}`).join('\n');
    const fullText = `Question: ${q.text}\n\nOptions:\n${optionsText}\n\nExplanation:\n${q.explanation || 'N/A'}`;
    navigator.clipboard.writeText(fullText);
    toast({
      title: 'Question Copied',
      description: 'Question text and options copied to clipboard.',
      status: 'success',
      duration: 2500,
      isClosable: true
    });
  };

  if (loading) return <Loading message="Aggregating master question bank..." />;

  return (
    <Flex flexDir="column" w="100%" maxW={{ base: '100%', '2xl': '1600px' }} mx="auto" p={{ base: 3, md: 5 }}>
      {/* Header Banner */}
      <Flex
        w="100%"
        p={6}
        bgGradient="linear(to-r, #0284c7, #0ea5e9, #06b6d4, #10b981)"
        borderRadius="2xl"
        mb={8}
        alignItems="center"
        justifyContent="space-between"
        color="white"
        boxShadow="0 12px 32px rgba(14, 165, 233, 0.25)"
        position="relative"
        overflow="hidden"
        flexWrap="wrap"
        gap={4}
      >
        <Box zIndex={2}>
          <Heading size="lg" fontWeight="black" letterSpacing="tight" mb={1}>
            Master Question Bank
          </Heading>
          <Text fontSize="sm" opacity={0.9}>
            Centralized pool of all course exam questions, chapter quizzes, image annotations, Doppler sound recordings, and clinical explanations.
          </Text>
        </Box>

        <HStack spacing={3} zIndex={2} flexWrap="wrap">
          <Flex bg="whiteAlpha.200" backdropFilter="blur(10px)" px={4} py={2} borderRadius="xl" align="center" gap={2}>
            <FontAwesomeIcon icon={faListCheck} />
            <Text fontWeight="bold" fontSize="sm">{stats.total} Total Questions</Text>
          </Flex>
          <Flex bg="whiteAlpha.200" backdropFilter="blur(10px)" px={4} py={2} borderRadius="xl" align="center" gap={2}>
            <FontAwesomeIcon icon={faImage} />
            <Text fontWeight="bold" fontSize="sm">{stats.withImages} with Scans</Text>
          </Flex>
          {stats.withAudio > 0 && (
            <Flex bg="whiteAlpha.200" backdropFilter="blur(10px)" px={4} py={2} borderRadius="xl" align="center" gap={2}>
              <FontAwesomeIcon icon={faMusic} />
              <Text fontWeight="bold" fontSize="sm">{stats.withAudio} with Audio</Text>
            </Flex>
          )}
        </HStack>
      </Flex>

      {/* Stats Summary Cards */}
      <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={4} mb={6}>
        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="teal.50" color="teal.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faQuestionCircle} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.total}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>Total Questions</Text>
          </Box>
        </Flex>

        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="green.50" color="green.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faGraduationCap} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.beginner}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>Beginner Level</Text>
          </Box>
        </Flex>

        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="orange.50" color="orange.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faGraduationCap} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.intermediate}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>Intermediate Level</Text>
          </Box>
        </Flex>

        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="pink.50" color="pink.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faGraduationCap} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.advanced}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>Advanced Level</Text>
          </Box>
        </Flex>
      </SimpleGrid>

      {/* Filter and Search Bar */}
      <Flex
        w="100%"
        bg={useColorModeValue('rgba(255, 255, 255, 0.75)', 'rgba(26, 32, 44, 0.75)')}
        backdropFilter="blur(20px)"
        p={2}
        pl={4}
        borderRadius="full"
        boxShadow={useColorModeValue('0 8px 32px 0 rgba(31, 38, 135, 0.05)', '0 8px 32px 0 rgba(0, 0, 0, 0.2)')}
        border="1px solid"
        borderColor={useColorModeValue('rgba(226, 232, 240, 0.8)', 'rgba(45, 55, 72, 0.8)')}
        mb={6}
        flexDir={{ base: 'column', md: 'row' }}
        alignItems="center"
        gap={2}
        position="relative"
        zIndex={2}
      >
        {/* Search Input */}
        <InputGroup size="lg" flex={1}>
          <InputLeftElement pointerEvents="none" h="full" color="green.400" pl={2}>
            <FontAwesomeIcon icon={faSearch} />
          </InputLeftElement>
          <Input
            placeholder="Search questions by stem, explanation, options, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="transparent"
            border="none"
            _focus={{ boxShadow: 'none' }}
            fontSize="md"
            fontWeight="600"
            pl={10}
          />
        </InputGroup>

        <Flex gap={2} w={{ base: '100%', md: 'auto' }} flexWrap="wrap" alignItems="center" pr={2}>
          {/* Difficulty Level Filter */}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              borderRadius="full"
              leftIcon={<FontAwesomeIcon icon={faFilter} color="#0ea5e9" />}
              fontWeight="700"
              fontSize="sm"
            >
              Level: {selectedLevel}
            </MenuButton>
            <Portal>
              <MenuList shadow="xl" borderRadius="xl" border="none" bg={useColorModeValue('white', 'gray.800')} p={2} zIndex={1500}>
                {['All', 'Beginner', 'Intermediate', 'Advanced'].map((lvl) => (
                  <MenuItem key={lvl} py={2.5} borderRadius="lg" fontWeight={selectedLevel === lvl ? 'bold' : 'normal'} onClick={() => setSelectedLevel(lvl)}>
                    {lvl === 'All' ? 'All Levels' : `${lvl} Level`}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>

          {/* Course Filter */}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              borderRadius="full"
              leftIcon={<FontAwesomeIcon icon={faFolderOpen} color="#0ea5e9" />}
              fontWeight="700"
              fontSize="sm"
            >
              {selectedCourse === 'All' ? 'All Courses' : uniqueCourses.find((c) => c.id === selectedCourse)?.name || 'Course'}
            </MenuButton>
            <Portal>
              <MenuList shadow="xl" borderRadius="xl" border="none" maxH="300px" overflowY="auto" bg={useColorModeValue('white', 'gray.800')} p={2} zIndex={1500}>
                <MenuItem py={2.5} borderRadius="lg" fontWeight={selectedCourse === 'All' ? 'bold' : 'normal'} onClick={() => setSelectedCourse('All')}>
                  All Courses
                </MenuItem>
                {uniqueCourses.map((c) => (
                  <MenuItem key={c.id} py={2.5} borderRadius="lg" fontWeight={selectedCourse === c.id ? 'bold' : 'normal'} onClick={() => setSelectedCourse(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      </Flex>

      {/* Tabs */}
      <Flex mb={6} justify="flex-start">
        <Tabs variant="soft-rounded" index={activeTab === 'all' ? 0 : activeTab === 'images' ? 1 : activeTab === 'audio' ? 2 : 3} onChange={(idx) => {
          if (idx === 0) setActiveTab('all');
          else if (idx === 1) setActiveTab('images');
          else if (idx === 2) setActiveTab('audio');
          else setActiveTab('textOnly');
        }}>
          <TabList bg={useColorModeValue('white', 'gray.900')} p={1} borderRadius="full" border="1px solid" borderColor={useColorModeValue('gray.200', 'gray.800')} gap={1}>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>All Questions ({questions.length})</Tab>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>With Images / Ultrasound ({stats.withImages})</Tab>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>With Audio ({stats.withAudio})</Tab>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>Text Only ({questions.length - stats.withImages - stats.withAudio})</Tab>
          </TabList>
        </Tabs>
      </Flex>

      {/* Question Cards List */}
      {filteredQuestions.length === 0 ? (
        <Flex p={12} bg={glassBg} borderRadius="2xl" border={glassBorder} direction="column" align="center" justify="center" textAlign="center">
          <Flex w="56px" h="56px" bg="teal.50" color="#0ea5e9" borderRadius="full" align="center" justify="center" fontSize="xl" mb={3}>
            <FontAwesomeIcon icon={faSearch} />
          </Flex>
          <Heading size="md" color={titleColor} mb={1}>No questions found</Heading>
          <Text fontSize="sm" color="gray.500">Try changing your search keywords or filter options.</Text>
        </Flex>
      ) : (
        <VStack spacing={4} align="stretch">
          {filteredQuestions.map((q, idx) => {
            const levelColor = (q.level || 'Beginner') === 'Advanced' ? 'pink' : (q.level || 'Beginner') === 'Intermediate' ? 'orange' : 'teal';

            return (
              <MotionBox
                key={`${q.courseId}-${q.quizId}-${q.id || idx}`}
                bg={glassBg}
                borderRadius="2xl"
                border={glassBorder}
                p={5}
                backdropFilter="blur(20px)"
                shadow="sm"
                whileHover={{ y: -2, shadow: 'md' }}
                transition={{ duration: 0.2 }}
              >
                {/* Top Question Metadata Bar */}
                <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
                  <HStack spacing={2}>
                    <Badge colorScheme={levelColor} borderRadius="full" px={2.5} py={0.5} fontSize="2xs" fontWeight="bold">
                      {q.level || 'Beginner'}
                    </Badge>
                    <Tooltip label="Course and Quiz Source" placement="top" hasArrow>
                      <Badge
                        colorScheme="green"
                        variant="subtle"
                        borderRadius="md"
                        px={2}
                        py={0.5}
                        fontSize="2xs"
                        fontWeight="bold"
                        cursor="pointer"
                        onClick={() => navigate(`/${q.courseId}/editor`)}
                      >
                        <FontAwesomeIcon icon={faFolderOpen} style={{ marginRight: '4px' }} />
                        {q.courseName} • {q.quizTitle}
                      </Badge>
                    </Tooltip>
                    {q.questionType === 'survey' && (
                      <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={2.5} py={0.5} fontSize="2xs" fontWeight="bold">
                        📊 Survey
                      </Badge>
                    )}
                    {q.images && q.images.length > 0 && (
                      <Badge colorScheme="purple" borderRadius="full" px={2} py={0.5} fontSize="2xs">
                        <FontAwesomeIcon icon={faImage} style={{ marginRight: '4px' }} />
                        {q.images.length} Media Attached
                      </Badge>
                    )}
                    {q.audioUrl && (
                      <Badge colorScheme="teal" borderRadius="full" px={2} py={0.5} fontSize="2xs">
                        <FontAwesomeIcon icon={faMusic} style={{ marginRight: '4px' }} />
                        Audio Attached
                      </Badge>
                    )}
                  </HStack>

                  {/* Actions */}
                  <HStack spacing={1.5}>
                    <Tooltip label="Test Question Interactively" placement="top" hasArrow>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="green"
                        leftIcon={<FontAwesomeIcon icon={faEye} />}
                        onClick={() => {
                          setTestingQuestion(q);
                          setSelectedOption(null);
                          setShowAnswer(false);
                        }}
                      >
                        Interactive Test
                      </Button>
                    </Tooltip>
                    <Tooltip label="Copy Question & Options" placement="top" hasArrow>
                      <IconButton
                        aria-label="Copy Question"
                        icon={<FontAwesomeIcon icon={faCopy} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        borderRadius="md"
                        onClick={() => copyQuestionText(q)}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>

                {/* Question Stem */}
                <Heading size="sm" color={titleColor} fontWeight="bold" lineHeight="tall" mb={4}>
                  <Text as="span" color="green.500" mr={2}>Q{idx + 1}.</Text>
                  {q.text}
                </Heading>

                {/* Attached Image Previews if any */}
                {q.images && q.images.length > 0 && (
                  <Flex gap={3} mb={4} flexWrap="wrap">
                    {q.images.map((img, imgIdx) => (
                      <Box
                        key={imgIdx}
                        borderRadius="xl"
                        overflow="hidden"
                        border="1px solid"
                        borderColor={useColorModeValue('gray.200', 'gray.700')}
                        maxW="140px"
                        maxH="100px"
                        minW="100px"
                        minH="80px"
                        bg={useColorModeValue('gray.100', 'gray.850')}
                        cursor="pointer"
                        onClick={async () => {
                          const signed = await getSignedS3Url(s3, img.url);
                          setZoomImageUrl(signed || img.url);
                        }}
                        _hover={{ transform: 'scale(1.03)', shadow: 'md' }}
                        transition="all 0.2s"
                        position="relative"
                      >
                        <SignedImage s3Client={s3} src={img.url} alt="Question Reference" w="100%" h="100%" maxH="100px" objectFit="cover" />
                        <Flex position="absolute" bottom={1} right={1} bg="blackAlpha.700" color="white" px={1.5} py={0.5} borderRadius="md" fontSize="2xs">
                          <FontAwesomeIcon icon={faEye} />
                        </Flex>
                      </Box>
                    ))}
                  </Flex>
                )}

                {/* Attached Audio Player if any */}
                {q.audioUrl && (
                  <Box mb={4} maxW="500px">
                    <QuizAudioPlayer s3Client={s3} audioUrl={q.audioUrl} title={q.audioTitle} compact />
                  </Box>
                )}

                {/* Answer Choices Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2.5} mb={3}>
                  {q.options.map((opt, optIdx) => {
                    const isCorrect = q.questionType !== 'survey' && optIdx === q.correctAnswer;
                    return (
                      <Flex
                        key={optIdx}
                        p={2.5}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor={isCorrect ? useColorModeValue('green.300', 'green.600') : useColorModeValue('gray.200', 'gray.700')}
                        bg={isCorrect ? useColorModeValue('green.50/70', 'green.950/30') : useColorModeValue('gray.50/50', 'whiteAlpha.50')}
                        align="center"
                        gap={2.5}
                      >
                        <Flex
                          w="22px"
                          h="22px"
                          borderRadius="full"
                          bg={isCorrect ? 'green.500' : useColorModeValue('gray.200', 'gray.700')}
                          color={isCorrect ? 'white' : useColorModeValue('gray.600', 'gray.300')}
                          align="center"
                          justify="center"
                          fontSize="2xs"
                          fontWeight="bold"
                          flexShrink={0}
                        >
                          {String.fromCharCode(65 + optIdx)}
                        </Flex>
                        <Text fontSize="xs" fontWeight={isCorrect ? 'bold' : 'medium'} color={isCorrect ? useColorModeValue('green.800', 'green.200') : titleColor} flex={1}>
                          {opt}
                        </Text>
                        {isCorrect && (
                          <Badge colorScheme="green" variant="solid" borderRadius="full" fontSize="3xs" px={1.5}>
                            Correct
                          </Badge>
                        )}
                      </Flex>
                    );
                  })}
                </SimpleGrid>

                {/* Explanation Accordion */}
                {q.explanation && (
                  <Accordion allowToggle>
                    <AccordionItem border="none">
                      <AccordionButton px={0} py={1} _hover={{ bg: 'transparent' }}>
                        <Flex align="center" gap={1.5} color="green.500" fontSize="xs" fontWeight="bold">
                          <FontAwesomeIcon icon={faLightbulb} />
                          <Text>View Clinical Explanation</Text>
                        </Flex>
                        <AccordionIcon color="green.500" ml={1} />
                      </AccordionButton>
                      <AccordionPanel px={3} py={2.5} bg={useColorModeValue('green.50/40', 'gray.850')} borderRadius="xl" mt={1}>
                        <Text fontSize="xs" color={useColorModeValue('gray.700', 'gray.300')} lineHeight="tall">
                          {q.explanation}
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}
              </MotionBox>
            );
          })}
        </VStack>
      )}

      {/* Interactive Question Testing Modal */}
      <Modal isOpen={testingQuestion !== null} onClose={() => setTestingQuestion(null)} size="2xl" isCentered>
        <ModalOverlay backdropFilter="blur(8px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" bg={useColorModeValue('white', 'gray.900')}>
          <ModalHeader pb={2}>
            <Flex align="center" justify="space-between" pr={6}>
              <Box overflow="hidden">
                <Heading size="sm" color={titleColor} noOfLines={1}>Test Question Simulation</Heading>
                <Text fontSize="xs" color="gray.400" mt={0.5}>{testingQuestion?.courseName} • {testingQuestion?.quizTitle}</Text>
              </Box>
              <Badge colorScheme={(testingQuestion?.level || 'Beginner') === 'Advanced' ? 'pink' : (testingQuestion?.level || 'Beginner') === 'Intermediate' ? 'orange' : 'teal'} borderRadius="full" px={2.5} py={0.5} fontSize="xs">
                {testingQuestion?.level || 'Beginner'}
              </Badge>
            </Flex>
          </ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody p={6} pt={2}>
            {testingQuestion && (
              <Stack spacing={4}>
                <Text fontSize="md" fontWeight="bold" color={titleColor}>
                  {testingQuestion.text}
                </Text>

                {testingQuestion.images && testingQuestion.images.length > 0 && (
                  <Flex justify="center" bg={useColorModeValue('gray.100', 'blackAlpha.700')} p={2} borderRadius="xl">
                    <SignedImage s3Client={s3} src={testingQuestion.images[0].url} alt="Medical Reference" maxH="220px" objectFit="contain" borderRadius="lg" />
                  </Flex>
                )}

                {testingQuestion.audioUrl && (
                  <QuizAudioPlayer s3Client={s3} audioUrl={testingQuestion.audioUrl} title={testingQuestion.audioTitle} />
                )}

                <VStack spacing={2} align="stretch">
                  {testingQuestion.options.map((opt, optIdx) => {
                    const isSelected = selectedOption === optIdx;
                    const isSurvey = testingQuestion.questionType === 'survey';
                    const isCorrect = !isSurvey && optIdx === testingQuestion.correctAnswer;
                    let optionBg = useColorModeValue('gray.50', 'gray.800');
                    let optionBorder = useColorModeValue('gray.200', 'gray.700');

                    if (showAnswer) {
                      if (isSurvey) {
                        if (isSelected) {
                          optionBg = useColorModeValue('purple.50', 'purple.950/40');
                          optionBorder = 'purple.500';
                        }
                      } else if (isCorrect) {
                        optionBg = useColorModeValue('green.50', 'green.950/40');
                        optionBorder = 'green.500';
                      } else if (isSelected) {
                        optionBg = useColorModeValue('red.50', 'red.950/40');
                        optionBorder = 'red.500';
                      }
                    } else if (isSelected) {
                      optionBg = isSurvey ? useColorModeValue('purple.50', 'purple.950/30') : useColorModeValue('green.50', 'green.950/30');
                      optionBorder = isSurvey ? 'purple.400' : 'green.400';
                    }

                    return (
                      <Flex
                        key={optIdx}
                        p={3}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor={optionBorder}
                        bg={optionBg}
                        align="center"
                        gap={3}
                        cursor={showAnswer ? 'default' : 'pointer'}
                        onClick={() => !showAnswer && setSelectedOption(optIdx)}
                        _hover={showAnswer ? {} : { borderColor: 'green.400' }}
                        transition="all 0.2s"
                      >
                        <Flex
                          w="24px"
                          h="24px"
                          borderRadius="full"
                          bg={isSelected ? 'green.500' : useColorModeValue('gray.200', 'gray.700')}
                          color={isSelected ? 'white' : useColorModeValue('gray.600', 'gray.300')}
                          align="center"
                          justify="center"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          {String.fromCharCode(65 + optIdx)}
                        </Flex>
                        <Text fontSize="sm" fontWeight={isSelected ? 'bold' : 'normal'} color={titleColor} flex={1}>
                          {opt}
                        </Text>
                        {showAnswer && isCorrect && <FontAwesomeIcon icon={faCheckCircle} color="#10b981" />}
                        {showAnswer && isSelected && !isCorrect && <FontAwesomeIcon icon={faTimesCircle} color="#ef4444" />}
                      </Flex>
                    );
                  })}
                </VStack>

                {!showAnswer ? (
                  <Button
                    colorScheme={testingQuestion.questionType === 'survey' ? 'purple' : 'green'}
                    isDisabled={selectedOption === null}
                    onClick={() => setShowAnswer(true)}
                    borderRadius="full"
                    mt={2}
                  >
                    {testingQuestion.questionType === 'survey' ? 'Submit Survey Response' : 'Submit Answer'}
                  </Button>
                ) : testingQuestion.questionType === 'survey' ? (
                  <Box p={4} bg={useColorModeValue('purple.50/50', 'gray.850')} borderRadius="xl" border="1px solid" borderColor={useColorModeValue('purple.200', 'gray.700')}>
                    <Flex align="center" gap={2} mb={1} color="purple.500">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <Text fontWeight="bold" fontSize="sm">
                        Survey Response Recorded!
                      </Text>
                    </Flex>
                    <Text fontSize="xs" color={useColorModeValue('gray.700', 'gray.300')} mt={1}>
                      Thank you for your response. Survey questions are ungraded and used for clinical feedback.
                    </Text>
                  </Box>
                ) : (
                  <Box p={4} bg={useColorModeValue('green.50/50', 'gray.850')} borderRadius="xl" border="1px solid" borderColor={useColorModeValue('green.200', 'gray.700')}>
                    <Flex align="center" gap={2} mb={1} color={selectedOption === testingQuestion.correctAnswer ? 'green.500' : 'red.500'}>
                      <FontAwesomeIcon icon={selectedOption === testingQuestion.correctAnswer ? faCheckCircle : faTimesCircle} />
                      <Text fontWeight="bold" fontSize="sm">
                        {selectedOption === testingQuestion.correctAnswer ? 'Correct Answer!' : 'Incorrect'}
                      </Text>
                    </Flex>
                    <Text fontSize="xs" color={useColorModeValue('gray.700', 'gray.300')} mt={1}>
                      {testingQuestion.explanation || 'No clinical explanation provided.'}
                    </Text>
                  </Box>
                )}
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Image Zoom Modal */}
      <Modal isOpen={zoomImageUrl !== null} onClose={() => setZoomImageUrl(null)} size="2xl" isCentered>
        <ModalOverlay backdropFilter="blur(8px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" bg="transparent" shadow="none">
          <ModalCloseButton color="white" bg="blackAlpha.600" borderRadius="full" />
          <ModalBody p={0}>
            {zoomImageUrl && (
              <SignedImage s3Client={s3} src={zoomImageUrl} alt="Zoomed Reference" w="100%" borderRadius="2xl" shadow="2xl" />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
